<template>
    <Dialog :visible="showDialog" class="action-popup" :modal="true" :draggable="false">
        
        <template #container>
            <div class="dialog-header">
                Assign User(s)
            </div>
            <div class="dialog-content">

                <div class="flex flex-column gap-1 mb-3">
                    <span class="text">You want to manage authorized users for the following licenses:</span>
                    
                    <DataTable :value="selectedLicenses" size="small" tableStyle="min-width: 100%" :row-class="() => 'row'" class="mb-3" scrollable scrollHeight="flex" style="max-height: 25vh;">
                        <Column field="serial_number" header="Serial number">
                            <template #body="slotProps">
                                <span class="text-serial-number">{{ slotProps.data.serial_number }}</span>
                            </template>
                        </Column>
                        <Column field="product.application" header="Application"></Column>
                        <Column field="product.exp" header="Expiration">
                            <template #body="slotProps">
                                <span v-if="slotProps.data.product.exp === '-1'" class="font-italic">None</span>
                                <span v-else>{{format(new Date(slotProps.data.product.exp), 'MMM do, yyyy')}}</span>
                            </template>
                        </Column>
                    </DataTable>
                </div>

                <div class="flex flex-column gap-2">

                    <div class="flex flex-column">

                        <div class="w-full flex flex-row justify-content-between align-items-center">
                            <div class="subtitle p-0 m-0 w-full justify-self-start flex flex-row align-items-center justify-content-start gap-2">
                                <span>Authorized Users</span>
                                <Button class="m-0 p-0" rounded text @click="showAddNewUserDialog = true" style="height: 100%; border-radius: 980px;">
                                    <SvgPlusCircle className="pluscircle-svg" color="var(--button-normal-color)"/>
                                </Button>
                            </div>
                            <span v-if="usersModifiedStates.some((obj) => obj.state !== usersInitialStates.find((o) => o.id === obj.id).state)" class="reset-text align-self-end" @click="resetUsersSelection">Reset</span>
                        </div>

                        <div v-if="usersModifiedStates" class="mt-2" style="border: 1px solid var(--table-border-color); width: 100%; border-radius: var(--my-border-radius);">
                            <ScrollPanel style="max-height: 15rem; height: 15rem; min-height: 0px; width: 100%;" class="px-1">
                                <div style="padding: 0px; width: 100%;">
                                    <DataView :value="allUsersSorted"  class="w-full px-1 my-1">
                                        <template #empty>
                                            <div class="w-full flex flex-row justify-content-start align-items-center py-1" style="background-color: var(--table-row-color);">
                                                <span>You haven't added any users yet. Get started by using the button above or do it through the <span style="font-weight: var(--font-weight-bold);">Users</span> panel.</span>
                                            </div>
                                        </template>
                                        <template #list="slotProps">
                                            <div class="grid grid-nogutter">
                                                <div v-for="(item, index) in slotProps.items" :key="index" class="px-0 delegates-item flex flex-row align-items-center gap-2 w-full" style="padding: 0.3rem 0.5rem; font-size: small; color: var(--theme-font-color);"
                                                    :class="{
                                                        'is-added-user': usersModifiedStates.find((obj) => obj.id === item.id).state === 1 && usersInitialStates.find((obj) => obj.id === item.id).state !== 1,
                                                        'is-removed-user': usersModifiedStates.find((obj) => obj.id === item.id).state === -1 && usersInitialStates.find((obj) => obj.id === item.id).state !== -1
                                                    }"
                                                >
                                                    <Checkbox :binary="true" v-model="usersModifiedStatesCheckbox.find((obj) => obj.id === item.id).checkboxState" @click="modifyCheckboxState(item.id)" :class="{'checkbox-mixed': usersModifiedStates.find((obj) => obj.id === item.id).state === 0}">
                                                        <template #icon>
                                                            <SvgLineHoriz v-if="usersModifiedStates.find((obj) => obj.id === item.id).state === 0" className="checkbox-icon-svg" color="var(--checkbox-icon-color)"/>
                                                        </template>
                                                    </Checkbox>
                                                    <span class="subtext">{{item.first_name}} {{item.last_name}} ({{item.email}})</span>
                                                </div>
                                            </div>
                                        </template>
                                    </DataView>
                                </div>
                            </ScrollPanel>
                        </div>
                    </div>
                </div>
            </div>
            <div class="dialog-footer">
                <div class="buttons">
                    <ButtonModule type="secondary" severity="normal" label="Cancel" @click="emit('close')"/>
                    <ButtonModule type="primary" severity="normal" label="Save Changes" @click="clickAssign" :disabled="!(usersModifiedStates.some((obj) => obj.state !== usersInitialStates.find((o) => o.id === obj.id).state))"/>
                </div>
            </div>
        </template>
    </Dialog>

    <!-- DIALOGS -->

    <!-- Add a New User window -->
    <AddNewUserDialog :showDialog="showAddNewUserDialog" :linkedLicenses="selectedLicenses" @close="showAddNewUserDialog = false" @added-new-user="(userInfo) => addedUser(userInfo)"/>
</template>

<script setup>
// --- IMPORT ---
import { ref, toRefs, computed, onMounted } from 'vue';
// PrimeVue
import Dialog from 'primevue/dialog'
import DataTable from 'primevue/datatable'
import Button from 'primevue/button'
import Column from 'primevue/column'
import ScrollPanel from 'primevue/scrollpanel'
import DataView from 'primevue/dataview'
import Checkbox from 'primevue/checkbox'
// vue
import ButtonModule from '@/components/Modules/ButtonModule'
import AddNewUserDialog from '@/components/Dialogs/Actions/AddNewUserDialog.vue'
import SvgPlusCircle from '@/components/SVGs/SvgPlusCircle.vue';
import SvgLineHoriz from '@/components/SVGs/SvgLineHoriz.vue';
// Others
import toastService from '@/composables/toastService';
import { format } from 'date-fns';
import UserUtils from '@/composables/UserUtils';
import UsersUtils from '@/composables/UsersUtils';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router'

const store = useStore()
const router = useRouter()
const { logoutUser } = UserUtils(store, router)
const { allUsers } = UsersUtils(store)

// API
import useApiRequest from '@/composables/HTTPS/useApiRequest';
const { fetchEditDelegates } = useApiRequest()
const httpEditDelegatesLoading = ref(false)
const httpEditDelegatesData = ref(null)

// --- EMITS ---
const emit = defineEmits(['close', 'refresh-licenses-table'])

// --- PROPS ---
const props = defineProps({
    showDialog: Boolean,
    selectedLicenses: Array
})
const { showDialog, selectedLicenses } = toRefs(props)

// --- VARIABLES ---
const allUsersSorted = computed(() => {
    return allUsers.value.sort((a,b) => { // SORT IN THE ALPHABETICAL ORDER
        const nameA = a.first_name.toLowerCase() + a.last_name.toLowerCase()
        const nameB = b.first_name.toLowerCase() + b.last_name.toLowerCase()
        if (nameA < nameB) { return -1 }
        else if (nameA > nameB) { return 1 }
        else { return 0 }
    })
})

const usersInitialStates = ref(null)
const usersModifiedStates = ref(null)
onMounted (() => {
    if (showDialog.value) {
        usersInitialStates.value = allUsersSorted.value.map((u) => {
            var initialState;
            const n = selectedLicenses.value.filter((license) => license.delegates.some((d) => d.id === u.id)).length
            if (n === 0) { initialState = -1 } // Attach to NONE of the selected licenses
            else if (n < selectedLicenses.value.length) { initialState = 0 } // Attach to some (but not all) of the selected licenses
            else { initialState = 1 } // Attach to ALL the selected licenses
            return {
                'id': u.id,
                'state': initialState
            }

        })
        resetUsersSelection()
    }
})
const usersModifiedStatesCheckbox = computed(() => {
    if (usersModifiedStates.value) {
        return usersModifiedStates.value.map((obj) => {
            return {
                'id': obj.id,
                'checkboxState': obj.state > 0
            }
        })
    }
    else { return null; }
})

// Clicked checkbox
const modifyCheckboxState = (id) => {
    const currentState = usersModifiedStates.value.find((obj) => obj.id === id).state
    if (currentState === 1) { usersModifiedStates.value.find((obj) => obj.id === id).state = -1 }
    else { usersModifiedStates.value.find((obj) => obj.id === id).state = 1 }
}

// Reset
const resetUsersSelection = () => {
    usersModifiedStates.value = usersInitialStates.value.map((obj) => { // Initialization of the copy
        return {
            'id': obj.id,
            'state': obj.state
        }
    })
}

// - Add a New User -
const showAddNewUserDialog = ref(false)
const addedUser = (userInfo) => {
    usersInitialStates.value = [...usersInitialStates.value.filter(u => u.id !== userInfo.id), {'id': userInfo.id, 'state': -1}]
    usersModifiedStates.value = [...usersModifiedStates.value.filter(u => u.id !== userInfo.id), {'id': userInfo.id, 'state': 1}]
}

// --- Validate attach/detach ---
const clickAssign = async () => {
    // Build object containing concerned licenses, users to be attached, users to be detached
    const usersToBeAttached = allUsersSorted.value.map((u) => u.id).filter((id) => usersModifiedStates.value.find((obj) => obj.id === id).state === 1 && usersInitialStates.value.find((obj) => obj.id === id).state !== 1)
    const usersToBeDetached = allUsersSorted.value.map((u) => u.id).filter((id) => usersModifiedStates.value.find((obj) => obj.id === id).state === -1 && usersInitialStates.value.find((obj) => obj.id === id).state !== -1)
    const licensesUpdatedDelegates = selectedLicenses.value.map((license) => {
        const temp = license.delegates.map((d) => d.id)
        // Add the delegates to be attached
        const tempAfterAttach = temp.concat(usersToBeAttached)
        // Delete the delegates to be detached
        const tempAfterAttachAndDetach = tempAfterAttach.filter((delegateId) => !usersToBeDetached.some((idToDetach) => idToDetach===delegateId))
        // Remove duplicated
        const updatedDelegates = tempAfterAttachAndDetach.filter((value, index, self) => { // REMOVE DUPLICATED
            return self.findIndex(obj =>
                obj === value
            ) === index;
        });
        return { serial_number: license.serial_number, delegates: updatedDelegates }
    })
    // Send HTTP request with passwordVerification
    httpEditDelegatesLoading.value = true
    try {
        httpEditDelegatesData.value = await fetchEditDelegates({ licenses_modifications: licensesUpdatedDelegates })
        let thereIsError = false
        let thereIsSuccess = false
        httpEditDelegatesData.value.licenses_updated.forEach((licenseInfo) => {
            if (licenseInfo.status !== "success") {
                thereIsError = true
                toastService.add({ severity: licenseInfo.status === 'warning' ? 'warn' : 'error', summary: `Edit Authorized Users: ${licenseInfo.serial_number}`, detail: licenseInfo.message, life: 5000 });
            }
            else {
                thereIsSuccess = true
                //toastService.add({ severity: 'success', summary: `Edit Authorized Users: ${licenseInfo.serial_number}`, detail: 'The authorized users for the license have been successfully updated.', life: 5000 });
            }
            // IF THERE IS "delegates" in the license object, update delegates in the cache
            const updatedDelegates = licenseInfo["delegates"]
            if (updatedDelegates) { store.commit('licenses/updateLicenseDelegates', { serial_number: licenseInfo.serial_number, delegates: updatedDelegates }) }
        })
        if (thereIsSuccess) { // show this if at least one success
            //toastService.add({ severity: 'success', summary: "Edit Authorized Users: Success", detail: `The authorized users for the ${thereIsError ? 'other' : ''} licenses have been successfully updated.`, life: 5000 });
        }
        // Close
        emit('close')
    } catch (err) {
        handleEditDelegatesError(err)
    } finally {
      httpEditDelegatesLoading.value = false
    }
}
// Handle an error from the server
const handleEditDelegatesError = (error) => {
    try {
        if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
            toastService.add({ severity: 'error', summary: `Edit Authorized Users: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
            return;
        }
        const errorStatus = error.response.status
        const errorCode = error.response.data.error_code
        const errorMessage = error.response.data.error_message
        switch (errorStatus) {
            case 400: // ACCESS_TOKEN_MISSING / MISSING_OBJ / MISSING_OBJ_ELEM (400)
                if (error.response.data.error_code == 'ACCESS_TOKEN_MISSING') {
                    toastService.add({ severity: 'error', summary: "Edit Authorized Users: Authorization Issue.", detail: 'You are not logged in.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else if (error.response.data.error_code == 'MISSING_OBJ' || error.response.data.error_code == 'MISSING_OBJ_ELEM') { // MISSING_OBJ / MISSING_OBJ_ELEM
                    toastService.add({ severity: 'warn', summary: "Edit Authorized Users: Missing Information.", detail: 'It seems that the request body does not contain the necessary information.', life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `Edit Authorized Users: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 401: // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID (401)
                if (errorCode == 'ACCESS_TOKEN_EXPIRED' || errorCode == 'ACCESS_TOKEN_INVALID') { // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID
                    // Logout: Do nothing, the interceptor should have made a refresh token request!
                    // logoutUser()
                }
                else {  toastService.add({ severity: 'error', summary: `Edit Authorized Users: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 404: // ACCOUNT_NOT_FOUND (404) => toast + logout
                if (errorCode == 'ACCOUNT_NOT_FOUND') { // ACCOUNT_NOT_FOUND
                    toastService.add({ severity: 'error', summary: "Edit Authorized Users: Account Not Found.", detail: 'The account no more exists.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else {  toastService.add({ severity: 'error', summary: `Edit Authorized Users: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 500: // DATABASE_ERROR / INTERNAL_ERROR (500) => toast
                if (errorCode == 'DATABASE_ERROR' || errorCode == 'INTERNAL_ERROR') {
                    toastService.add({ severity: 'error', summary: "Edit Authorized Users: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
                    showPasswordVerificationDialog.value = false
                }
                else {  toastService.add({ severity: 'error', summary: `Edit Authorized Users: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            default:
                toastService.add({ severity: 'error', summary: `Edit Authorized Users: Error ${errorStatus}.`, detail: errorMessage, life: 12000 });
                break;
        }
    } catch {
        toastService.add({ severity: 'error', summary: `Edit Authorized Users: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}
</script>

<style scoped>
:deep(.p-checkbox .p-checkbox-box .pi) {
    transform: scale(0.8);
    font-weight: 50;
}
:deep(.p-checkbox.checkbox-mixed .p-checkbox-box) {
    background-color: var(--checkbox-mixed-background-color) !important;
}
</style>